import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Button, Collapse } from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useDrop } from 'react-dnd'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { hashContentPath, unHashContentPath } from 'common/src/ContentPath'
import { FormulaSheetEntry } from 'common/src/formulaSheet/types'
import styles from './FormulaSheetEditor.module.less'
import {
    autoOpenFormulaSheetEntryIdAtom,
    formulaSheetEntriesAtom,
    formulaSheetEntriesHaveUnsavedChangesSelector,
    formulaSheetEntriesWithUnsavedChangesSelector,
    lastSavedFormulaSheetEntriesAtom,
} from '@/atoms/formulaSheetEntries'
import {
    isActiveCourseReadOnlySelector,
    frontendDisplayedCourseSelector,
} from '@/atoms/auth'
import {
    hashedContentPathsInCanonicalOrderSelector,
    nodeNameByHashedPrefixSelector,
} from '@/atoms/contentTreeDerived'
import { agentActivityAtom } from '@/atoms/agentActivity'
import { AgentContentJobMode } from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { AddFormulaSheetEntryButton } from './AddFormulaSheetEntryButton/AddFormulaSheetEntryButton'
import { FormulaSheetDownloadButton } from './FormulaSheetDownloadButton/FormulaSheetDownloadButton'
import { FormulaSheetSectionGroup } from './FormulaSheetSectionGroup'
import { FormulaSheetAgentSurface } from './FormulaSheetAgentSurface'
import { SectionCheckpointsPanel } from '@/components/ContentTreeNode/ContentTreeLeafNode/SectionCheckpointsButton/SectionCheckpointsPanel'
import { overwriteFormulaSheetEntries } from '@/api/cb/contentCreatorContent'
import { syncSuccessMessage, syncErrorMessage } from '@/utils/syncMessages'
import { logger } from '@/logging/FrontendLogger'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { createEmptyFormulaSheetEntry } from './immer'
import { DndProviderWrapper } from '@/components/utils/DraggableSortingList/DraggableSortingList'
import { DraggableItem } from '@/components/utils/DraggableItem/DraggableItem'

const UNASSIGNED_GROUP_KEY = 'unassigned'

// An open path through the nested accordions. leaf is a section hash or the
// unassigned key; topic/subtopic are hashed id-path prefixes. Partial chains
// (only a topic open) are valid
interface OpenChain {
    topic: string | null
    subtopic: string | null
    leaf: string | null
}

const CLOSED_CHAIN: OpenChain = { topic: null, subtopic: null, leaf: null }

interface GroupHeaderProps {
    groupKey: string
    title: string
    // Absent on topic/subtopic headers: they open on drag-hover but entries
    // can only be dropped on sections
    onDropEntry?: (sourceListId: string, sourceIndex: number) => void
    onOpenSection?: () => void
    // Open this group while a drag hovers its header. The drag's SOURCE group
    // must stay open too — hiding the drag source aborts an HTML5 drag — so
    // the hover reports the source list id and the parent keeps both open
    onDragHoverOpen: (sourceListId: string) => void
}

const GroupHeader: React.FC<GroupHeaderProps> = (props): ReactElement => {
    // Latest props behind a ref so the drop spec never re-registers mid-drag
    // (re-registration ends the in-flight drag — see DraggableItem)
    const propsRef = useRef(props)
    propsRef.current = props
    const [{ isOver, hoverSourceListId }, drop] = useDrop<
        { index: number; listId?: string },
        unknown,
        { isOver: boolean; hoverSourceListId: string | null }
    >(
        () => ({
            accept: DraggableItem.name,
            collect: (monitor) => ({
                isOver:
                    monitor.isOver() &&
                    !!propsRef.current.onDropEntry &&
                    monitor.getItem()?.listId !== propsRef.current.groupKey,
                hoverSourceListId: monitor.isOver()
                    ? monitor.getItem()?.listId ?? null
                    : null,
            }),
            drop: (item) => {
                const { groupKey, onDropEntry } = propsRef.current
                if (!onDropEntry) return
                if (!item.listId || item.listId === groupKey) return
                onDropEntry(item.listId, item.index)
            },
        }),
        [props.groupKey]
    )
    const { onDragHoverOpen } = props
    const lastHoverSourceRef = useRef<string | null>(null)
    useEffect(() => {
        if (
            hoverSourceListId &&
            lastHoverSourceRef.current !== hoverSourceListId
        ) {
            onDragHoverOpen(hoverSourceListId)
        }
        lastHoverSourceRef.current = hoverSourceListId
    }, [hoverSourceListId, onDragHoverOpen])
    return (
        <div
            ref={drop}
            className={`${styles.groupHeader} ${
                isOver ? styles.groupHeaderDropTarget : ''
            }`}
        >
            <span>{props.title}</span>
            {props.onOpenSection && (
                <Button
                    type={'link'}
                    size={'small'}
                    onClick={(event) => {
                        event.stopPropagation()
                        props.onOpenSection?.()
                    }}
                >
                    Open section
                </Button>
            )}
        </div>
    )
}

export const FormulaSheetEditor: React.FC<unknown> = (): ReactElement => {
    const [formulaSheetEntries, setFormulaSheetEntries] = useRecoilState(
        formulaSheetEntriesAtom
    )
    const setLastSavedFormulaSheetEntries = useSetRecoilState(
        lastSavedFormulaSheetEntriesAtom
    )
    const dirtyEntryIds = useRecoilValue(
        formulaSheetEntriesWithUnsavedChangesSelector
    )
    const hasUnsavedChanges = useRecoilValue(
        formulaSheetEntriesHaveUnsavedChangesSelector
    )
    const canonicalHashes = useRecoilValue(
        hashedContentPathsInCanonicalOrderSelector
    )
    const nodeNames = useRecoilValue(nodeNameByHashedPrefixSelector)
    const validHashSet = useMemo(
        () => new Set(canonicalHashes),
        [canonicalHashes]
    )
    const entriesByHash = useMemo(() => {
        const groups = new Map<string, FormulaSheetEntry[]>()
        canonicalHashes.forEach((hash) => groups.set(hash, []))
        formulaSheetEntries.forEach((entry) => {
            if (
                entry.hashedContentPath &&
                validHashSet.has(entry.hashedContentPath)
            ) {
                groups.get(entry.hashedContentPath)?.push(entry)
            }
        })
        return groups
    }, [canonicalHashes, formulaSheetEntries, validHashSet])
    const unassignedEntries = useMemo(
        () =>
            formulaSheetEntries.filter(
                (entry) =>
                    !entry.hashedContentPath ||
                    !validHashSet.has(entry.hashedContentPath)
            ),
        [formulaSheetEntries, validHashSet]
    )
    const stalePathByEntryId = useMemo(
        () =>
            new Map(
                unassignedEntries
                    .filter((entry) => !!entry.hashedContentPath)
                    .map((entry) => [
                        entry.id,
                        entry.hashedContentPath as string,
                    ])
            ),
        [unassignedEntries]
    )
    // Nested grouping mirroring the content tree: topic → subtopic → section.
    // Every section renders, including empty ones — a section with no entries
    // yet is exactly where the instructor needs the section-scoped Generate
    const topicTree = useMemo(() => {
        interface LeafGroup {
            hash: string
            entries: FormulaSheetEntry[]
        }
        interface SubtopicGroup {
            key: string
            leaves: LeafGroup[]
            count: number
        }
        interface TopicGroup {
            key: string
            subtopics: SubtopicGroup[]
            count: number
        }
        const topics: TopicGroup[] = []
        canonicalHashes.forEach((hash) => {
            const path = unHashContentPath(hash)
            const topicKey = hashContentPath(path.slice(0, 1))
            const subtopicKey = hashContentPath(
                path.slice(0, Math.max(1, path.length - 1))
            )
            let topic = topics[topics.length - 1]
            if (!topic || topic.key !== topicKey) {
                topic = { key: topicKey, subtopics: [], count: 0 }
                topics.push(topic)
            }
            let subtopic = topic.subtopics[topic.subtopics.length - 1]
            if (!subtopic || subtopic.key !== subtopicKey) {
                subtopic = { key: subtopicKey, leaves: [], count: 0 }
                topic.subtopics.push(subtopic)
            }
            const entries = entriesByHash.get(hash) ?? []
            subtopic.leaves.push({ hash, entries })
            subtopic.count += entries.length
            topic.count += entries.length
        })
        return topics
    }, [canonicalHashes, entriesByHash])
    // A leaf key resolves to its full open chain through the accordions
    const chainForLeafKey = useCallback((leafKey: string | null): OpenChain => {
        if (!leafKey) return CLOSED_CHAIN
        if (leafKey === UNASSIGNED_GROUP_KEY) {
            return { topic: null, subtopic: null, leaf: leafKey }
        }
        const path = unHashContentPath(leafKey)
        return {
            topic: hashContentPath(path.slice(0, 1)),
            subtopic: hashContentPath(
                path.slice(0, Math.max(1, path.length - 1))
            ),
            leaf: leafKey,
        }
    }, [])

    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const activity = useRecoilValue(agentActivityAtom)
    const isCourseFormulaJobActive =
        activity?.activeJobs.some(
            (job) =>
                job.courseName === courseName &&
                job.mode === AgentContentJobMode.formulaSheet
        ) ?? false
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const saveFormulaSheetEntries = useCallback(async (): Promise<void> => {
        setIsSaving(true)
        let error
        try {
            const response = await overwriteFormulaSheetEntries(
                courseName,
                formulaSheetEntries
            )
            if (!response.data.success) error = response.data.error
        } catch (caughtError) {
            error = caughtError
        }
        setIsSaving(false)
        if (error) {
            logger.error(`Failed to save formula sheet with error: ${error}`)
            syncErrorMessage('Failed to save formula sheet. Please try again.')
        } else {
            setLastSavedFormulaSheetEntries(formulaSheetEntries)
            syncSuccessMessage('Formula sheet successfully saved')
        }
    }, [courseName, formulaSheetEntries, setLastSavedFormulaSheetEntries])

    const reorderGroup = useCallback(
        (groupIds: string[]): void => {
            const groupIdSet = new Set(groupIds)
            let groupIndex = 0
            setFormulaSheetEntries((entries) => {
                const entryById = new Map(
                    entries.map((entry) => [entry.id, entry])
                )
                return entries.map((entry) =>
                    groupIdSet.has(entry.id)
                        ? (entryById.get(
                              groupIds[groupIndex++]
                          ) as FormulaSheetEntry)
                        : entry
                )
            })
        },
        [setFormulaSheetEntries]
    )
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const [viewingState, setViewingState] = useRecoilState(viewingStateAtom)
    const setAutoOpenEntryId = useSetRecoilState(
        autoOpenFormulaSheetEntryIdAtom
    )
    // One open chain on normal clicks; during a drag the SOURCE chain and the
    // hovered target chain are both open (collapsing the source kills the
    // drag), so panels open/close dynamically as the drag moves
    const [clickChain, setClickChain] = useState<OpenChain>(CLOSED_CHAIN)
    const [dragChains, setDragChains] = useState<{
        source: OpenChain
        hover: OpenChain
    } | null>(null)
    const openSingleGroup = useCallback(
        (leafKey: string | null): void => {
            setClickChain(chainForLeafKey(leafKey))
            setDragChains(null)
        },
        [chainForLeafKey]
    )
    const openForDragHover = useCallback(
        (targetChain: OpenChain, sourceListId: string): void => {
            const sourceChain = chainForLeafKey(sourceListId)
            setDragChains((current) => {
                const next = { source: sourceChain, hover: targetChain }
                return current &&
                    JSON.stringify(current) === JSON.stringify(next)
                    ? current
                    : next
            })
        },
        [chainForLeafKey]
    )
    const openChains = useMemo(
        () =>
            dragChains
                ? [clickChain, dragChains.source, dragChains.hover]
                : [clickChain],
        [clickChain, dragChains]
    )
    const outerActiveKeys = useMemo(
        () =>
            openChains
                .map((chain) =>
                    chain.leaf === UNASSIGNED_GROUP_KEY
                        ? UNASSIGNED_GROUP_KEY
                        : chain.topic
                )
                .filter((key): key is string => !!key),
        [openChains]
    )
    const subtopicActiveKeys = useCallback(
        (topicKey: string): string[] =>
            openChains
                .filter((chain) => chain.topic === topicKey && chain.subtopic)
                .map((chain) => chain.subtopic as string),
        [openChains]
    )
    const leafActiveKeys = useCallback(
        (subtopicKey: string): string[] =>
            openChains
                .filter((chain) => chain.subtopic === subtopicKey && chain.leaf)
                .map((chain) => chain.leaf as string),
        [openChains]
    )
    const getGroupKey = useCallback(
        (entry: FormulaSheetEntry): string =>
            entry.hashedContentPath && validHashSet.has(entry.hashedContentPath)
                ? entry.hashedContentPath
                : UNASSIGNED_GROUP_KEY,
        [validHashSet]
    )
    const addEntryToGroup = useCallback(
        (groupKey: string): void => {
            const entry = {
                ...createEmptyFormulaSheetEntry(),
                hashedContentPath:
                    groupKey === UNASSIGNED_GROUP_KEY ? null : groupKey,
            }
            setFormulaSheetEntries((entries) => {
                const lastGroupIndex = entries.reduce(
                    (lastIndex, candidate, index) =>
                        getGroupKey(candidate) === groupKey ? index : lastIndex,
                    -1
                )
                const nextEntries = [...entries]
                nextEntries.splice(
                    lastGroupIndex === -1
                        ? nextEntries.length
                        : lastGroupIndex + 1,
                    0,
                    entry
                )
                return nextEntries
            })
            openSingleGroup(groupKey)
            setAutoOpenEntryId(entry.id)
        },
        [
            getGroupKey,
            openSingleGroup,
            setAutoOpenEntryId,
            setFormulaSheetEntries,
        ]
    )
    const moveEntryToGroup = useCallback(
        (
            targetGroupKey: string,
            sourceListId: string,
            sourceIndex: number,
            beforeEntryId?: string
        ): void => {
            if (isReadOnly || sourceListId === targetGroupKey) return
            const sourceEntries =
                sourceListId === UNASSIGNED_GROUP_KEY
                    ? unassignedEntries
                    : entriesByHash.get(sourceListId) ?? []
            const draggedEntryId = sourceEntries[sourceIndex]?.id
            if (!draggedEntryId) return
            setFormulaSheetEntries((entries) => {
                const sourceGlobalIndex = entries.findIndex(
                    (entry) => entry.id === draggedEntryId
                )
                if (sourceGlobalIndex === -1) return entries
                const nextEntries = [...entries]
                const [draggedEntry] = nextEntries.splice(sourceGlobalIndex, 1)
                const reassignedEntry = {
                    ...draggedEntry,
                    hashedContentPath:
                        targetGroupKey === UNASSIGNED_GROUP_KEY
                            ? null
                            : targetGroupKey,
                }
                const beforeIndex = beforeEntryId
                    ? nextEntries.findIndex(
                          (entry) => entry.id === beforeEntryId
                      )
                    : -1
                if (beforeIndex !== -1) {
                    nextEntries.splice(beforeIndex, 0, reassignedEntry)
                    return nextEntries
                }
                const lastTargetIndex = nextEntries.reduce(
                    (lastIndex, candidate, index) =>
                        getGroupKey(candidate) === targetGroupKey
                            ? index
                            : lastIndex,
                    -1
                )
                nextEntries.splice(
                    lastTargetIndex === -1
                        ? nextEntries.length
                        : lastTargetIndex + 1,
                    0,
                    reassignedEntry
                )
                return nextEntries
            })
            openSingleGroup(targetGroupKey)
        },
        [
            entriesByHash,
            getGroupKey,
            isReadOnly,
            openSingleGroup,
            setFormulaSheetEntries,
            unassignedEntries,
        ]
    )
    // Deep links (entry id / section path) are consumed ONCE per navigation,
    // tracked via refs. Never write viewingState back from here: clearing it
    // races the URL sync in useViewingState, which restores the stale query
    // and re-pins the accordion on every bounce
    const consumedDeepLinkIdRef = useRef<string | null>(null)
    const consumedDeepLinkHashRef = useRef<string | null>(null)
    useEffect(() => {
        if (viewingState.viewingStateType !== ViewingStateType.formulaSheet) {
            consumedDeepLinkIdRef.current = null
            consumedDeepLinkHashRef.current = null
            return
        }
        if (viewingState.id) {
            if (consumedDeepLinkIdRef.current === viewingState.id) return
            const entry = formulaSheetEntries.find(
                (candidate) => candidate.id === viewingState.id
            )
            if (!entry) return
            consumedDeepLinkIdRef.current = viewingState.id
            openSingleGroup(getGroupKey(entry))
            setAutoOpenEntryId(entry.id)
            return
        }
        if (viewingState.contentPath.length > 0) {
            const requestedHash = hashContentPath(viewingState.contentPath)
            if (consumedDeepLinkHashRef.current === requestedHash) return
            consumedDeepLinkHashRef.current = requestedHash
            if (validHashSet.has(requestedHash)) {
                openSingleGroup(requestedHash)
            }
        }
    }, [
        formulaSheetEntries,
        getGroupKey,
        openSingleGroup,
        setAutoOpenEntryId,
        validHashSet,
        viewingState,
    ])

    return (
        <div className={styles.formulaSheetEditor}>
            <div className={styles.formulaSheetEditorTitle}>Formula Sheet</div>
            <div className={styles.formulaSheetEditorToolbar}>
                <Button
                    loading={isSaving}
                    disabled={!hasUnsavedChanges}
                    onClick={() => void saveFormulaSheetEntries()}
                    type={'primary'}
                    icon={<SaveOutlined />}
                >
                    Save Changes
                </Button>
                <AddFormulaSheetEntryButton
                    onAdd={() =>
                        addEntryToGroup(clickChain.leaf ?? UNASSIGNED_GROUP_KEY)
                    }
                />
                <FormulaSheetDownloadButton />
            </div>
            <FormulaSheetAgentSurface
                contentPath={[]}
                entryCount={formulaSheetEntries.length}
                isCourseFormulaJobActive={isCourseFormulaJobActive}
            />
            <Collapse>
                <Collapse.Panel header={'Checkpoints'} key={'checkpoints'}>
                    <SectionCheckpointsPanel contentPath={[]} />
                </Collapse.Panel>
            </Collapse>
            <DndProviderWrapper className={styles.dndProviderWrapper}>
                <Collapse
                    activeKey={outerActiveKeys}
                    onChange={(keys) => {
                        // Click behavior stays single-open: keep only the
                        // newly opened key (or none when closing)
                        const keyList = Array.isArray(keys)
                            ? keys
                            : keys
                              ? [keys]
                              : []
                        const added = keyList.find(
                            (key) => !outerActiveKeys.includes(key)
                        )
                        if (!added) {
                            openSingleGroup(null)
                        } else if (added === UNASSIGNED_GROUP_KEY) {
                            openSingleGroup(UNASSIGNED_GROUP_KEY)
                        } else {
                            setClickChain({
                                topic: added,
                                subtopic: null,
                                leaf: null,
                            })
                            setDragChains(null)
                        }
                    }}
                >
                    <Collapse.Panel
                        forceRender
                        key={UNASSIGNED_GROUP_KEY}
                        header={
                            <GroupHeader
                                groupKey={UNASSIGNED_GROUP_KEY}
                                title={`Unassigned (${unassignedEntries.length})`}
                                onDropEntry={(sourceListId, sourceIndex) =>
                                    moveEntryToGroup(
                                        UNASSIGNED_GROUP_KEY,
                                        sourceListId,
                                        sourceIndex
                                    )
                                }
                                onDragHoverOpen={(sourceListId) =>
                                    openForDragHover(
                                        chainForLeafKey(UNASSIGNED_GROUP_KEY),
                                        sourceListId
                                    )
                                }
                            />
                        }
                    >
                        <FormulaSheetSectionGroup
                            contentPath={null}
                            entries={unassignedEntries}
                            dirtyEntryIds={dirtyEntryIds}
                            isReadOnly={isReadOnly}
                            isCourseFormulaJobActive={isCourseFormulaJobActive}
                            onReorder={reorderGroup}
                            stalePathByEntryId={stalePathByEntryId}
                            listId={UNASSIGNED_GROUP_KEY}
                            onAdd={() => addEntryToGroup(UNASSIGNED_GROUP_KEY)}
                            onExternalDrop={(
                                sourceListId,
                                sourceIndex,
                                beforeIndex
                            ) =>
                                moveEntryToGroup(
                                    UNASSIGNED_GROUP_KEY,
                                    sourceListId,
                                    sourceIndex,
                                    unassignedEntries[beforeIndex]?.id
                                )
                            }
                        />
                    </Collapse.Panel>
                    {topicTree.map((topic) => (
                        <Collapse.Panel
                            forceRender
                            key={topic.key}
                            header={
                                <GroupHeader
                                    groupKey={topic.key}
                                    title={`${nodeNames.get(topic.key) ?? topic.key} (${topic.count})`}
                                    onDragHoverOpen={(sourceListId) =>
                                        openForDragHover(
                                            {
                                                topic: topic.key,
                                                subtopic: null,
                                                leaf: null,
                                            },
                                            sourceListId
                                        )
                                    }
                                />
                            }
                        >
                            <Collapse
                                activeKey={subtopicActiveKeys(topic.key)}
                                onChange={(keys) => {
                                    const keyList = Array.isArray(keys)
                                        ? keys
                                        : keys
                                          ? [keys]
                                          : []
                                    const added = keyList.find(
                                        (key) =>
                                            !subtopicActiveKeys(
                                                topic.key
                                            ).includes(key)
                                    )
                                    setClickChain({
                                        topic: topic.key,
                                        subtopic: added ?? null,
                                        leaf: null,
                                    })
                                    setDragChains(null)
                                }}
                            >
                                {topic.subtopics.map((subtopic) => (
                                    <Collapse.Panel
                                        forceRender
                                        key={subtopic.key}
                                        header={
                                            <GroupHeader
                                                groupKey={subtopic.key}
                                                title={`${nodeNames.get(subtopic.key) ?? subtopic.key} (${subtopic.count})`}
                                                onDragHoverOpen={(
                                                    sourceListId
                                                ) =>
                                                    openForDragHover(
                                                        {
                                                            topic: topic.key,
                                                            subtopic:
                                                                subtopic.key,
                                                            leaf: null,
                                                        },
                                                        sourceListId
                                                    )
                                                }
                                            />
                                        }
                                    >
                                        <Collapse
                                            activeKey={leafActiveKeys(
                                                subtopic.key
                                            )}
                                            onChange={(keys) => {
                                                const keyList = Array.isArray(
                                                    keys
                                                )
                                                    ? keys
                                                    : keys
                                                      ? [keys]
                                                      : []
                                                const added = keyList.find(
                                                    (key) =>
                                                        !leafActiveKeys(
                                                            subtopic.key
                                                        ).includes(key)
                                                )
                                                if (added) {
                                                    openSingleGroup(added)
                                                } else {
                                                    setClickChain({
                                                        topic: topic.key,
                                                        subtopic: subtopic.key,
                                                        leaf: null,
                                                    })
                                                    setDragChains(null)
                                                }
                                            }}
                                        >
                                            {subtopic.leaves.map((leaf) => (
                                                <Collapse.Panel
                                                    forceRender
                                                    key={leaf.hash}
                                                    header={
                                                        <GroupHeader
                                                            groupKey={leaf.hash}
                                                            title={`${nodeNames.get(leaf.hash) ?? leaf.hash} (${leaf.entries.length})`}
                                                            onDropEntry={(
                                                                sourceListId,
                                                                sourceIndex
                                                            ) =>
                                                                moveEntryToGroup(
                                                                    leaf.hash,
                                                                    sourceListId,
                                                                    sourceIndex
                                                                )
                                                            }
                                                            onOpenSection={() =>
                                                                setViewingState(
                                                                    {
                                                                        contentPath:
                                                                            unHashContentPath(
                                                                                leaf.hash
                                                                            ),
                                                                        viewingStateType:
                                                                            ViewingStateType.outline,
                                                                        id: null,
                                                                    }
                                                                )
                                                            }
                                                            onDragHoverOpen={(
                                                                sourceListId
                                                            ) =>
                                                                openForDragHover(
                                                                    chainForLeafKey(
                                                                        leaf.hash
                                                                    ),
                                                                    sourceListId
                                                                )
                                                            }
                                                        />
                                                    }
                                                >
                                                    <FormulaSheetSectionGroup
                                                        contentPath={unHashContentPath(
                                                            leaf.hash
                                                        )}
                                                        entries={leaf.entries}
                                                        dirtyEntryIds={
                                                            dirtyEntryIds
                                                        }
                                                        isReadOnly={isReadOnly}
                                                        isCourseFormulaJobActive={
                                                            isCourseFormulaJobActive
                                                        }
                                                        onReorder={reorderGroup}
                                                        listId={leaf.hash}
                                                        onAdd={() =>
                                                            addEntryToGroup(
                                                                leaf.hash
                                                            )
                                                        }
                                                        onExternalDrop={(
                                                            sourceListId,
                                                            sourceIndex,
                                                            beforeIndex
                                                        ) =>
                                                            moveEntryToGroup(
                                                                leaf.hash,
                                                                sourceListId,
                                                                sourceIndex,
                                                                leaf.entries[
                                                                    beforeIndex
                                                                ]?.id
                                                            )
                                                        }
                                                    />
                                                </Collapse.Panel>
                                            ))}
                                        </Collapse>
                                    </Collapse.Panel>
                                ))}
                            </Collapse>
                        </Collapse.Panel>
                    ))}
                </Collapse>
            </DndProviderWrapper>
        </div>
    )
}
